import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from 'primereact/button';
import axios from 'axios';
import { url } from '../../../../../env';
import BuroTableTK from '../buro/BuroTableTK';
import BuroPaymentHistoryTK from '../buro/BuroPaymentHistoryTK';

const BLUE = '#1565C0';

// ─── Helper: check if dataBuro has real content ──────────
const hasRealData = (d) => d && d.cards && d.tipo_persona;

// ─── Score color helper ──────────
const getScoreColor = (score, threshold) => {
    if (score == null) return { bg: '#f1f5f9', color: '#64748b' };
    if (score >= threshold) return { bg: '#dcfce7', color: '#16a34a' };
    if (score >= threshold * 0.7) return { bg: '#fef3c7', color: '#d97706' };
    return { bg: '#fee2e2', color: '#dc2626' };
};

// ─── Metric card ──────────
const MetricCard = ({ title, value, color = BLUE }) => (
    <div
        className="p-2 border-round shadow-1"
        style={{
            backgroundColor: '#F5F8FF',
            borderLeft: `3px solid ${color}`,
            flex: '1 1 calc(33.33% - 0.5rem)',
            minWidth: '120px',
        }}
    >
        <div className="text-xs text-500 mb-1" style={{ lineHeight: 1.2 }}>{title}</div>
        <div className="text-sm font-bold" style={{ color }}>{value ?? '—'}</div>
    </div>
);

// ─── Skeleton loader ──────────
const Skeleton = () => (
    <>
        <style>{`@keyframes buroSkel{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        <div className="flex flex-column gap-3 p-3">
            <div style={{ height: '3rem', background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', borderRadius: '4px', animation: 'buroSkel 1.4s infinite' }} />
            <div style={{ height: '10rem', background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', borderRadius: '4px', animation: 'buroSkel 1.4s infinite' }} />
            <div style={{ height: '16rem', background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', borderRadius: '4px', animation: 'buroSkel 1.4s infinite' }} />
        </div>
    </>
);

// ─── PF Tables ──────────
const PFTables = ({ data }) => (
    <div className="flex flex-column gap-3">
        {data?.nombresClientes && (
            <BuroTableTK data={data.nombresClientes.data} columns={data.nombresClientes.configuration} name={data.nombresClientes.name} />
        )}
        {data?.domicilios && (
            <BuroTableTK data={data.domicilios.data} columns={data.domicilios.configuration} name={data.domicilios.name} />
        )}
        {data?.empleos && (
            <BuroTableTK data={data.empleos.data} columns={data.empleos.configuration} name={data.empleos.name} />
        )}
        {data?.cuentas && (
            <BuroTableTK data={data.cuentas.data} columns={data.cuentas.configuration} name={data.cuentas.name} />
        )}
        {data?.consultasEfectuadas && (
            <BuroTableTK data={data.consultasEfectuadas.data} columns={data.consultasEfectuadas.configuration} name={data.consultasEfectuadas.name} />
        )}
        {data?.hawkAlertBd && (
            <BuroTableTK data={data.hawkAlertBd.data} columns={data.hawkAlertBd.configuration} name={data.hawkAlertBd.name} />
        )}
        {data?.declaracionesCliente && (
            <BuroTableTK data={data.declaracionesCliente.data} columns={data.declaracionesCliente.configuration} name={data.declaracionesCliente.name} />
        )}
        {data?.historialPagos?.[0] && (
            <BuroPaymentHistoryTK
                customerService={data.historialPagos[0]}
                name={data.historialPagos[0]?.name || 'Historial de Pagos'}
            />
        )}
    </div>
);

// ─── PM Tables ──────────
const PMTables = ({ data }) => (
    <div className="flex flex-column gap-3">
        {data?.datosGenerales && (
            <BuroTableTK data={data.datosGenerales.data} columns={data.datosGenerales.configuration} name={data.datosGenerales.name} />
        )}
        {data?.accionistas && (
            <BuroTableTK data={data.accionistas.data} columns={data.accionistas.configuration} name={data.accionistas.name} />
        )}
        {data?.historiaFinanciera && (
            <BuroTableTK data={data.historiaFinanciera.data} columns={data.historiaFinanciera.configuration} name={data.historiaFinanciera.name} />
        )}
        {data?.hawkHr && (
            <BuroTableTK data={data.hawkHr.data} columns={data.hawkHr.configuration} name={data.hawkHr.name} />
        )}
        {data?.hawkHc && (
            <BuroTableTK data={data.hawkHc.data} columns={data.hawkHc.configuration} name={data.hawkHc.name} />
        )}
        {data?.calificaciones && (
            <BuroTableTK data={data.calificaciones.data} columns={data.calificaciones.configuration} name={data.calificaciones.name} />
        )}
        {data?.historialConsultas && (
            <BuroTableTK data={data.historialConsultas.data} columns={data.historialConsultas.configuration} name={data.historialConsultas.name} />
        )}
        {data?.historialPagosFinancierosLiquidados?.length > 0 && (
            <BuroPaymentHistoryTK
                customerService={data.historialPagosFinancierosLiquidados}
                name={data.historialPagosFinancierosLiquidados[0]?.name || 'Historial de Pagos Financiero - LIQUIDADO'}
                variant="liquidado"
            />
        )}
        {data?.historialPagosFinancierosNoLiquidados?.length > 0 && (
            <BuroPaymentHistoryTK
                customerService={data.historialPagosFinancierosNoLiquidados}
                name={data.historialPagosFinancierosNoLiquidados[0]?.name || 'Historial de Pagos Financiero - ACTIVO'}
                variant="activo"
            />
        )}
        {data?.historialPagosComercial?.length > 0 && (
            <BuroPaymentHistoryTK
                customerService={data.historialPagosComercial[0]}
                name={data.historialPagosComercial[0]?.[0]?.name || 'Historial de Pagos Comercial'}
            />
        )}
    </div>
);

// ─── Main Component ──────────
const BuroAnalysisPanel = ({ persona, toast, dataBuro: dataBuroProp }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const hasFetched = useRef(false);

    const fetchData = useCallback(async () => {
        if (!persona?.id) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${url}/getReportBuro/${persona.id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            setData(response.data);
            hasFetched.current = true;
        } catch (error) {
            console.error('Error al obtener datos de Buró:', error);
            toast?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al obtener datos de Buró de Crédito',
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }, [persona?.id]);

    // Si el prop tiene data real, usarla
    useEffect(() => {
        if (hasRealData(dataBuroProp)) {
            setData(dataBuroProp);
            hasFetched.current = true;
        }
    }, [dataBuroProp]);

    // Siempre fetch al montar si no hay data real
    useEffect(() => {
        if (!hasFetched.current && !loading) {
            fetchData();
        }
    }, [fetchData, loading]);

    if (loading && !data) return <Skeleton />;

    if (!data || !data.cards) {
        return (
            <div className="flex flex-column align-items-center justify-content-center p-4 gap-3" style={{ minHeight: '10rem' }}>
                <i className="pi pi-info-circle text-3xl text-blue-400" />
                <span className="text-sm text-500">No se pudieron obtener datos de Buró de Crédito.</span>
                <Button
                    label="Reintentar"
                    icon="pi pi-refresh"
                    className="p-button-text p-button-sm"
                    onClick={fetchData}
                    loading={loading}
                />
            </div>
        );
    }

    const cards = data.cards;
    const isBuroPM = data.tipo_persona === 'PM';
    const scoreThreshold = isBuroPM ? 200 : 600;
    const scoreColors = getScoreColor(cards.score, scoreThreshold);
    const scoreLabel = cards.score >= scoreThreshold ? 'Bueno' : cards.score >= scoreThreshold * 0.7 ? 'Regular' : 'Bajo';

    return (
        <div className="flex flex-column gap-2">
            {/* Header compact: title + score inline */}
            <div
                className="flex align-items-center justify-content-between p-2 border-round-lg"
                style={{ background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)' }}
            >
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-check-circle text-lg" style={{ color: BLUE }} />
                    <span className="text-sm font-bold text-900">
                        Buró de Crédito {isBuroPM ? '(PM)' : '(PF)'}
                    </span>
                </div>
                <div className="flex align-items-center gap-2">
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-text p-button-rounded p-button-sm"
                        onClick={fetchData}
                        loading={loading}
                        tooltip="Actualizar"
                        tooltipOptions={{ position: 'top' }}
                    />
                </div>
            </div>

            {/* Score card compact — same size as MetricCards */}
            {cards?.score != null && (
                <div
                    className="p-2 border-round shadow-1 flex align-items-center justify-content-between"
                    style={{
                        backgroundColor: scoreColors.bg,
                        borderLeft: `3px solid ${scoreColors.color}`,
                    }}
                >
                    <div>
                        <div className="text-xs text-500 mb-1" style={{ lineHeight: 1.2 }}>Score de Crédito</div>
                        <div className="text-xl font-bold" style={{ color: scoreColors.color }}>
                            {cards.score}
                        </div>
                    </div>
                    <span
                        className="text-xs font-bold px-2 py-1 border-round"
                        style={{
                            backgroundColor: `${scoreColors.color}20`,
                            color: scoreColors.color,
                        }}
                    >
                        {scoreLabel}
                    </span>
                </div>
            )}

            {/* Metric cards */}
            <div className="flex flex-wrap gap-2">
                {isBuroPM ? (
                    <>
                        <MetricCard title="Créditos financieros" value={cards?.totalCreditosFinancieros} />
                        <MetricCard title="Créditos comerciales" value={cards?.totalCreditosComerciales} />
                        <MetricCard title="Saldo quita y quebranto" value={cards?.saldoAcumuladoQuitaQuebranto != null ? `$${cards.saldoAcumuladoQuitaQuebranto}` : undefined} color="#E65100" />
                        <MetricCard title="Créditos quita/quebranto" value={cards?.ratioQuitaQuebranto} color="#E65100" />
                        <MetricCard title="Saldo total financiero" value={cards?.saldoTotalFinanciero != null ? `$${cards.saldoTotalFinanciero}` : undefined} color="#23b56e" />
                        <MetricCard title="Saldo total comercial" value={cards?.saldoTotalComercial != null ? `$${cards.saldoTotalComercial}` : undefined} color="#23b56e" />
                    </>
                ) : (
                    <>
                        <MetricCard title="Obligaciones" value={cards?.obligaciones != null ? `$${cards.obligaciones}` : undefined} color="#34b9e0" />
                        <MetricCard title="Límite tarjetas de crédito" value={cards?.limiteTarjetas != null ? `$${cards.limiteTarjetas}` : undefined} />
                        <MetricCard title="Suma saldos actuales" value={cards?.sumaSaldoActual != null ? `$${cards.sumaSaldoActual}` : undefined} />
                        <MetricCard title="Cuentas abiertas" value={cards?.cuentasAbiertas} color="#23b56e" />
                        <MetricCard title="Cuentas vencidas" value={cards?.cuentasConSaldoVencido} color={(cards?.cuentasConSaldoVencido ?? 0) > 0 ? '#dc2626' : '#23b56e'} />
                    </>
                )}
            </div>

            {/* Tables */}
            {isBuroPM ? <PMTables data={data} /> : <PFTables data={data} />}
        </div>
    );
};

export default BuroAnalysisPanel;
